/**
 * Copyright (C) Siemens AG 1996-2024 ALL RIGHTS RESERVED.
 */

import {Component, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {Subscription} from 'rxjs';
import {themeSwitcher} from '@siemens/ix-angular';
import {UserService} from '../../../services/user.service';
import {Language, LanguageService} from '../../../services/language.service';
import {ThemeService} from '../../../services/theme.service';
import {LogoService} from '../services/logo/logo.service';

const DEFAULT_LOGO_WIDTH = 120;
const DEFAULT_LOGO_HEIGHT = 19.07;

@Component({
  selector: 'app-navbar',
  templateUrl: './navbar.component.html',
  styleUrl: './navbar.component.scss',
  standalone: false,
})
export class NavbarComponent implements OnInit, OnDestroy {
  private readonly userService = inject(UserService);
  private readonly languageService = inject(LanguageService);
  private readonly themeService = inject(ThemeService);
  private readonly logoService = inject(LogoService);

  protected readonly themeSwitcher = themeSwitcher;
  protected readonly sessionStorage = sessionStorage;
  selectedLanguage = sessionStorage.getItem('language') || 'en';
  availableLanguages: Language[] = [];

  private languageSubscription: Subscription | null = null;
  private schemaChangedDisposable: {dispose(): void} | null = null;

  /** Tracks whether the current theme variant is dark; updated on every theme change. */
  isDarkTheme = this.themeService.actualVariant === 'dark';

  @Input({alias: 'showLogo', required: false}) showLogo = true;
  @Input({
    alias: 'logoLightPath',
    required: false,
  })
  logoLightPath = 'assets/img/sie-logo-black-rgb.svg';
  @Input({
    alias: 'logoDarkPath',
    required: false,
  })
  logoDarkPath = 'assets/img/sie-logo-white-rgb.svg';

  /** `true` when the active logo is an inline `data:` URL (admin-uploaded brand logo). */
  get isInlineLogo(): boolean {
    const path = this.isDarkTheme ? this.logoDarkPath : this.logoLightPath;
    return path.startsWith('data:');
  }
  private logoWidthSet = false;
  @Input({alias: 'logoWidth', required: false})
  set logoWidth(value: number) {
    this._logoWidth = value;
    this.logoWidthSet = true;
  }
  get logoWidth(): number {
    return this._logoWidth;
  }
  private _logoWidth = DEFAULT_LOGO_WIDTH;

  private logoHeightSet = false;
  @Input({alias: 'logoHeight', required: false})
  set logoHeight(value: number) {
    this._logoHeight = value;
    this.logoHeightSet = true;
  }
  get logoHeight(): number {
    return this._logoHeight;
  }
  private _logoHeight = DEFAULT_LOGO_HEIGHT;

  ngOnInit() {
    // Load available languages from manifest
    this.languageSubscription = this.languageService
      .getAvailableLanguages()
      .subscribe((languages) => {
        this.availableLanguages = languages;
      });
    this.themeService.setTheme();
    // Sync initial value in case the theme was already set before this
    // component was created (e.g. by AppComponent for authenticated routes).
    this.isDarkTheme = this.themeService.actualVariant === 'dark';

    // Resolve logo dimensions from the logo service
    // Only set the logo dimensions if they haven't been explicitly set via inputs.
    const {width, height} = this.logoService.resolveLogoDimensions();
    if (!this.logoWidthSet && width !== undefined) {
      this.logoWidth = width;
    }
    if (!this.logoHeightSet && height !== undefined) {
      this.logoHeight = height;
    }

    this.schemaChangedDisposable = themeSwitcher.schemaChanged.on((variant) => {
      this.isDarkTheme = variant === 'dark';
    });
  }

  ngOnDestroy() {
    this.languageSubscription?.unsubscribe();
    this.schemaChangedDisposable?.dispose();
  }

  onToggleThemeClick() {
    themeSwitcher.toggleMode();
  }

  changeLanguage(language: string) {
    this.userService.changeLanguage(language);
    this.selectedLanguage = language;
  }

  getSelectedLanguageNativeName(): string {
    return this.languageService.getLanguageNativeName(this.selectedLanguage);
  }
}
